import { AdminRoute } from '@/components/admin/AdminRoute';
import { Shield, FileText, Database, Map, GitCompare, Loader2, MessageSquare, Users } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEffect, useState, lazy, Suspense } from 'react';
import { AdminVerificationProvider } from '@/components/admin/AdminVerificationContext';

// Lazy load heavy admin components
const AdminPassages = lazy(() => import('@/components/admin/AdminPassages'));
const AdminParagraphConversion = lazy(() => import('@/components/admin/AdminParagraphConversion'));
const AdminDataVerification = lazy(() => import('@/components/admin/AdminDataVerification'));
const AdminContextDataVerification = lazy(() => import('@/components/admin/AdminContextDataVerification'));
const AdminBackgroundData = lazy(() => import('@/components/admin/AdminBackgroundData'));
const AdminReferenceData = lazy(() => import('@/components/admin/AdminReferenceData'));
const AdminMigrations = lazy(() => import('@/components/admin/AdminMigrations').then(m => ({ default: m.AdminMigrations })));
const ModelComparison = lazy(() => import('@/components/admin/ModelComparison'));
const AdminFeedback = lazy(() => import('@/components/admin/AdminFeedback'));
const AdminUsers = lazy(() => import('@/components/admin/AdminUsers'));
const AdminBackgroundSources = lazy(() => import('@/components/admin/AdminBackgroundSources'));
const AdminPerformance = lazy(() => import('@/components/admin/AdminPerformance'));

const AdminLoading = () => (
    <div className="flex flex-col items-center justify-center p-12 space-y-4">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        <p className="text-sm text-muted-foreground italic">Loading admin module...</p>
    </div>
);

export default function AdminPage() {
    // Read tab from URL parameter, default to 'users'
    const [activeTab, setActiveTab] = useState<string>('users');

    useEffect(() => {
        // Read tab from URL on mount
        const params = new URLSearchParams(window.location.search);
        const tabParam = params.get('tab');
        const allowedTabs = ['background-data', 'reference-data', 'bible-text', 'model-comparison', 'feedback', 'users', 'sources'];
        if (tabParam && allowedTabs.includes(tabParam)) {
            setActiveTab(tabParam);
        }
    }, []);

    const handleTabChange = (value: string) => {
        setActiveTab(value);
        // Update URL parameter without reloading the page
        const url = new URL(window.location.href);
        url.searchParams.set('tab', value);
        window.history.pushState({}, '', url.toString());
    };

    return (
        <AdminRoute>
            <AdminVerificationProvider>
                <div className="w-full p-6">
                    <div className="mb-8">
                        <h1 className="text-3xl font-bold flex items-center gap-2">
                            <Shield className="w-8 h-8" />
                            Admin Dashboard
                        </h1>
                        <p className="text-muted-foreground mt-2">
                            Manage the Bible Glen application
                        </p>
                    </div>

                    <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full">
                        <TabsList className="grid w-full grid-cols-7 mb-6">
                            <TabsTrigger value="users" className="flex items-center gap-1 sm:gap-2 cursor-pointer">
                                <Users className="h-4 w-4 flex-shrink-0" />
                                <span className="hidden sm:inline">Users</span>
                            </TabsTrigger>
                            <TabsTrigger value="feedback" className="flex items-center gap-1 sm:gap-2 cursor-pointer">
                                <MessageSquare className="h-4 w-4 flex-shrink-0" />
                                <span className="hidden sm:inline">Feedback</span>
                            </TabsTrigger>
                            <TabsTrigger value="background-data" className="flex items-center gap-1 sm:gap-2 cursor-pointer">
                                <Database className="h-4 w-4 flex-shrink-0" />
                                <span className="hidden sm:inline">Background</span>
                            </TabsTrigger>
                            <TabsTrigger value="reference-data" className="flex items-center gap-1 sm:gap-2 cursor-pointer">
                                <Map className="h-4 w-4 flex-shrink-0" />
                                <span className="hidden sm:inline">Reference</span>
                            </TabsTrigger>
                            <TabsTrigger value="bible-text" className="flex items-center gap-1 sm:gap-2 cursor-pointer">
                                <FileText className="h-4 w-4 flex-shrink-0" />
                                <span className="hidden sm:inline">Text</span>
                            </TabsTrigger>
                            <TabsTrigger value="model-comparison" className="flex items-center gap-1 sm:gap-2 cursor-pointer">
                                <GitCompare className="h-4 w-4 flex-shrink-0" />
                                <span className="hidden sm:inline">Models</span>
                            </TabsTrigger>
                            <TabsTrigger value="sources" className="flex items-center gap-1 sm:gap-2 cursor-pointer">
                                <Shield className="h-4 w-4 flex-shrink-0" />
                                <span className="hidden sm:inline">Sources</span>
                            </TabsTrigger>
                        </TabsList>

                        <Suspense fallback={<AdminLoading />}>
                            <TabsContent value="background-data" className="space-y-6">
                                {activeTab === 'background-data' && (
                                    <>
                                        <AdminPerformance />
                                        <AdminContextDataVerification />
                                        <AdminMigrations />
                                        <AdminBackgroundData />
                                    </>
                                )}
                            </TabsContent>

                            <TabsContent value="reference-data" className="space-y-6">
                                {activeTab === 'reference-data' && <AdminReferenceData />}
                            </TabsContent>

                            <TabsContent value="bible-text" className="space-y-6">
                                {activeTab === 'bible-text' && (
                                    <div className="grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-6">
                                        <AdminDataVerification />
                                        <AdminParagraphConversion />
                                        <AdminPassages />
                                    </div>
                                )}
                            </TabsContent>

                            <TabsContent value="model-comparison" className="space-y-6">
                                {activeTab === 'model-comparison' && <ModelComparison />}
                            </TabsContent>

                            <TabsContent value="feedback" className="space-y-6">
                                {activeTab === 'feedback' && <AdminFeedback />}
                            </TabsContent>

                            <TabsContent value="users" className="space-y-6">
                                {activeTab === 'users' && <AdminUsers />}
                            </TabsContent>

                            <TabsContent value="sources" className="space-y-6">
                                {activeTab === 'sources' && <AdminBackgroundSources />}
                            </TabsContent>
                        </Suspense>
                    </Tabs>
                </div>
            </AdminVerificationProvider>
        </AdminRoute>
    );
}




